"use client";

import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { useUserQuery } from "../hooks/useUserQuery";
import { createInitialState, userReducer } from "../lib/user/reducer";
import {
  handleNonAdminUser,
  isUserAdmin,
  canAccessStaffPortal,
  isUserDataComplete,
} from "../lib/user/validation";
import { getUserFromCache, isCacheValid } from "../lib/user/storage";
import {
  DEFAULT_REFETCH_INTERVAL,
  HYDRATION_DELAY,
} from "../lib/user/constants";
import type {
  UserContextType,
  UserDisplayData,
  UserProviderProps,
} from "../lib/user/types";
import type { UserObjectType } from "../types/schema";

// Create context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider component
export function UserProvider({
  children,
  autoFetch = true,
  refetchInterval = DEFAULT_REFETCH_INTERVAL,
}: UserProviderProps) {
  const [state, dispatch] = useReducer(userReducer, createInitialState());
  const hasInitializedRef = useRef(false);
  const hasHydratedRef = useRef(false);

  // User query hook
  const userQuery = useUserQuery({
    onUserFetched: user => {
      dispatch({ type: "SET_USER", payload: user });
      if (user) {
        dispatch({ type: "SET_LAST_FETCH", payload: Date.now() });
      }
    },
    onError: error => {
      dispatch({ type: "SET_ERROR", payload: error });
    },
    onLoadingChange: loading => {
      if (!userQuery.isFetching) {
        dispatch({ type: "SET_LOADING", payload: loading });
      }
    },
  });

  // Actions
  const actions = {
    getMe: userQuery.fetchUser,
    logout: () => dispatch({ type: "LOGOUT" }),
    reset: () => dispatch({ type: "RESET" }),
    refreshUser: userQuery.fetchUser,
    refreshUserFresh: userQuery.fetchUserFresh,
    refreshUserBackground: userQuery.fetchUserBackground,

    setUser: async (user: UserObjectType | null) => {
      if (user && !canAccessStaffPortal(user)) {
        await handleNonAdminUser(user);
        return;
      }
      dispatch({ type: "SET_USER", payload: user });
    },

    setUserPartial: async (user: UserObjectType | null) => {
      if (user && !canAccessStaffPortal(user)) {
        await handleNonAdminUser(user);
        return;
      }
      dispatch({ type: "SET_USER_PARTIAL", payload: user });

      // Auto-hydrate in background if we have partial data
      if (user && !isUserDataComplete(user)) {
        setTimeout(() => {
          actions.hydrateUser();
        }, HYDRATION_DELAY);
      }
    },

    hydrateUser: async () => {
      // Only hydrate if we're not already loading and have partial data
      if (userQuery.isFetching || state.isHydrated || !state.user) {
        return;
      }

      try {
        await userQuery.fetchUserBackground();
      } catch (error) {
        console.error("Background hydration failed:", error);
      }
    },
  };

  // Client-side hydration effect
  useEffect(() => {
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      dispatch({ type: "HYDRATE_FROM_CACHE" });
    }
  }, []);

  // Auto-fetch user data after cache hydration
  useEffect(() => {
    if (autoFetch && !hasInitializedRef.current && hasHydratedRef.current) {
      hasInitializedRef.current = true;

      const cachedUser = getUserFromCache();
      const hasValidCache = cachedUser && isCacheValid();

      if (hasValidCache) {
        setTimeout(() => {
          userQuery.fetchUserBackground();
        }, HYDRATION_DELAY);
      } else {
        userQuery.fetchUser();
      }
    }
  }, [autoFetch, hasHydratedRef.current, userQuery]);

  // Periodic refresh
  useEffect(() => {
    if (!refetchInterval || refetchInterval <= 0) return;

    const interval = setInterval(() => {
      if (state.user && state.lastFetch && !userQuery.isFetching) {
        const timeSinceLastFetch = Date.now() - state.lastFetch;
        if (timeSinceLastFetch >= refetchInterval) {
          userQuery.fetchUser();
        }
      }
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [refetchInterval, state.user, state.lastFetch, userQuery]);

  // Update loading state based on query loading
  useEffect(() => {
    if (!userQuery.isFetching) {
      dispatch({ type: "SET_LOADING", payload: userQuery.loading });
    }
  }, [userQuery.loading, userQuery.isFetching]);

  // Update error state based on query error
  useEffect(() => {
    if (userQuery.error) {
      dispatch({ type: "SET_ERROR", payload: userQuery.error.message });
    }
  }, [userQuery.error]);

  const contextValue: UserContextType = {
    state,
    actions,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

// Custom hook to use the user context
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

// Convenience hooks for specific parts of the state
export function useUserData() {
  const { state } = useUser();
  return state.user;
}

export function useIsAuthenticated() {
  const { state } = useUser();
  return state.isAuthenticated;
}

export function useUserLoading() {
  const { state } = useUser();
  return state.isLoading;
}

export function useUserError() {
  const { state } = useUser();
  return state.error;
}

export function useUserActions() {
  const { actions } = useUser();
  return actions;
}

export function useRefreshUser() {
  const { refreshUserFresh } = useUserActions();
  return refreshUserFresh;
}

// Hydration-safe hooks
export function useUserDataSafe() {
  const { state } = useUser();

  if (!state.isClientSideHydrated) {
    return null;
  }

  return state.user;
}

export function useUserDisplaySafe(): UserDisplayData {
  const user = useUserDataSafe();

  return {
    fullName: user ? `${user.firstName} ${user.lastName}`.trim() : "",
    displayName: user?.username || user?.firstName || "User",
    initials: user
      ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase()
      : "",
    email: user?.email || "",
    avatar: user?.image?.url || "/avatars/default.jpg",
  };
}
